import React, { useEffect, useState } from 'react';
import { patientImageService } from '../../services/admin/patientImage.service';

interface PatientAvatarProps {
  patientId: string;
  name: string;
  profilePictureUrl?: string | null;
  size?: 'md' | 'lg';
}

const SIZE_CLASSES = {
  md: 'h-9 w-9',
  lg: 'h-10 w-10',
};

const PatientAvatar: React.FC<PatientAvatarProps> = ({
  patientId,
  name,
  profilePictureUrl,
  size = 'md',
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(profilePictureUrl || null);
  const [loading, setLoading] = useState(!profilePictureUrl);
  const [imageError, setImageError] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setImageError(false);

    if (profilePictureUrl && retryAttempt === 0) {
      setImageUrl(profilePictureUrl);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setImageUrl(null);
    setLoading(true);

    patientImageService.getPatientImage(patientId).then(result => {
      if (!cancelled) {
        setImageUrl(result.hasProfilePicture ? result.imageUrl : null);
        setLoading(false);
      }
    });
  }, [patientId, profilePictureUrl, retryAttempt]);

  const initials = name
    .split(' ')
    .filter(Boolean)
    .map(part => part.charAt(0))
    .slice(0, 2)
    .join('');
  const activeUrl = !loading && !imageError && (profilePictureUrl || imageUrl);
  const sizeClass = SIZE_CLASSES[size];

  if (activeUrl) {
    return (
      <img
        src={activeUrl}
        alt={name}
        className={`${sizeClass} rounded-full object-cover flex-shrink-0`}
        onError={() => {
          patientImageService.clearPatientImage(patientId);
          if (retryAttempt === 0) {
            setRetryAttempt(1);
          } else {
            setImageError(true);
          }
        }}
      />
    );
  }

  return (
    <div className={`${sizeClass} rounded-full bg-slate-700 text-white flex items-center justify-center flex-shrink-0`}>
      <span className="text-sm font-bold">{initials}</span>
    </div>
  );
};

export default PatientAvatar;
